import { Input, Section, Select } from "@telegram-apps/telegram-ui";
import React from "react";
import { Expense, ExpenseCategory, InputExpense } from "../../types";
import {
  EXPENSE_CATEGORIES,
  getCurrentTimeInGMT9,
  normalizeDate,
} from "../../utils";
import { categoryName } from "../Charts/cateogryName";
import Layout from "../Layout";

const evaluateAmount = (expression: string): number | undefined => {
  expression = expression.replace(/[^-()\d/*+.]/g, "");
  try {
    // eslint-disable-next-line no-eval
    const result = eval(expression);
    if (typeof result === "number" && !isNaN(result)) {
      return result;
    }
    return undefined;
  } catch {
    return undefined;
  }
};

export default function useExpenseView(
  expense?: Partial<Expense>,
  onTouched?: () => void
): {
  component: React.ReactNode;
  expense: InputExpense;
  callbacks: {
    setExpense: (expense: Expense) => void;
  };
} {
  const [date, _setDate] = React.useState<Date>(
    expense?.date ?? getCurrentTimeInGMT9()
  );
  const [category, _setCategory] = React.useState<ExpenseCategory>(
    (expense?.category as ExpenseCategory) ?? EXPENSE_CATEGORIES[0]
  );
  const [expression, _setExpression] = React.useState<string>(
    expense?.amount?.toString() ?? "0"
  );
  const [description, _setDescription] = React.useState<string>(
    expense?.description ?? ""
  );
  const [reportedBy, _setReportedBy] = React.useState<string>(
    expense?.reportedBy ?? ""
  );

  const amount = React.useMemo<number | undefined>(
    () => evaluateAmount(expression),
    [expression]
  );

  const valueChanged = React.useCallback(
    <T extends (...args: any[]) => any>(fn: T) => {
      return (...args: Parameters<T>) => {
        fn(...args);
        onTouched?.();
      };
    },
    [onTouched]
  );

  const setDate = React.useMemo(() => valueChanged(_setDate), [valueChanged]);
  const setCategory = React.useMemo(
    () => valueChanged(_setCategory),
    [valueChanged]
  );
  const setExpression = React.useMemo(
    () => valueChanged(_setExpression),
    [valueChanged]
  );
  const setDescription = React.useMemo(
    () => valueChanged(_setDescription),
    [valueChanged]
  );
  const setReportedBy = React.useMemo(
    () => valueChanged(_setReportedBy),
    [valueChanged]
  );

  const setExpense = React.useCallback(
    (expense: Expense) => {
      setDate(expense.date);
      setCategory(expense.category as ExpenseCategory);
      setExpression(expense.amount?.toString() ?? "0");
      setDescription(expense.description);
      setReportedBy(expense.reportedBy);
    },
    [setDate, setCategory, setExpression, setDescription, setReportedBy]
  );

  const callbacks = React.useMemo(
    () => ({
      setExpense,
    }),
    [setExpense]
  );

  const component = React.useMemo(() => {
    return (
      <Layout>
        <Section header="支出を編集">
          <Input
            header="日付け"
            type="date"
            value={normalizeDate(date)}
            onChange={(e) => setDate(new Date(e.target.value))}
          />
          <div style={{ display: "flex" }}>
            <div style={{ flexShrink: 0 }}>
              <Select
                header="種別"
                value={category}
                onChange={(e) => setCategory(e.target.value as ExpenseCategory)}
              >
                {EXPENSE_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {categoryName(c)}
                  </option>
                ))}
              </Select>
            </div>
            <Input
              header="金額"
              value={expression}
              onChange={(e) => setExpression(e.target.value)}
            />
          </div>
          <Input
            header="説明"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            type="text"
          />
          <Input header="報告者" value={reportedBy} disabled />
        </Section>
      </Layout>
    );
  }, [
    date,
    category,
    expression,
    description,
    reportedBy,
    setDate,
    setCategory,
    setExpression,
    setDescription,
  ]);

  return {
    component,
    expense: {
      date,
      category,
      amount,
      description,
      reportedBy,
    } as InputExpense,
    callbacks,
  };
}
